import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { SlidersHorizontal } from 'lucide-react';
import { useAlertsInbox, useAlertAction, useAlertDetail, useCaseCreate, useNoteCreate } from '../api/hooks';
import { SchoolApi } from '../api/client';
import { useAuth } from '../../utils/Auth/AuthProvider';
import AlertSeverityBadge from '../components/AlertSeverityBadge';
import SectionPicker from '../components/SectionPicker';
import { PanelCard, EmptyState, Badge, Button, PageHeader, PillTabs, Segmented, PagedFooter, Initials } from '../components/ui';
import usePagination from '../lib/usePagination';
import AlertPayloadDetail from '../components/ui/AlertPayloadDetail';
import { FaBell } from '../components/ui/icons';
import { compactGrade, whenLabel } from './dashboards/sections/format';
import { alertPlace, alertRisk, useStudentLookup } from './dashboards/sections/useStudentLookup';
import { alertSentence } from '../lib/alertSentence';
import { fieldClass } from '../lib/fieldClass';
import { nameOr } from '../lib/names';
import type { AlertInstance } from '../api/types';
import { LINK_BTN } from '../components/ui/linkButton';

const RULES_ROLES = ['school_admin', 'school', 'admin'];

const DOT: Record<string, string> = {
  critical: 'bg-danger',
  warning: 'bg-accent',
  info: 'bg-surface ring-2 ring-inset ring-slate',
};

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

function SeverityDot({ severity }: { severity: string }) {
  return <span aria-hidden="true" className={`mt-1.5 h-2.5 w-2.5 flex-none rounded-full ${DOT[severity] ?? DOT.info}`} />;
}

function Timeline({ alert, notified }: { alert: AlertInstance; notified: NonNullable<AlertInstance['notified']> }) {
  const steps: { label: string; when: string }[] = [{ label: 'Alert raised', when: `${whenLabel(alert.fired_at)} · system` }];
  // Staff the alert was pushed to (warning digests); critical pushes leave no record.
  notified.forEach((n) => steps.push({ label: 'Notice sent to staff', when: `${whenLabel(n.sent_at)} · ${n.name}` }));
  if (alert.snoozed_until) steps.push({ label: `Snoozed until ${fmtDate(alert.snoozed_until)}`, when: '' });
  if (alert.resolved_at) steps.push({ label: 'Resolved', when: fmtDate(alert.resolved_at) });
  return (
    <div>
      <p className="mb-3.5 font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Activity</p>
      <ol className="ml-1.5 border-l-2 border-line pl-[18px]">
        {steps.map((s, i) => (
          <li key={i} className="relative pb-4">
            <span aria-hidden="true" className="absolute -left-[24px] top-[5px] h-2.5 w-2.5 rounded-full border-2 border-ink bg-surface" />
            <p className="font-semibold text-ink">{s.label}</p>
            {s.when && <p className="text-xs text-muted">{s.when}</p>}
          </li>
        ))}
      </ol>
    </div>
  );
}

/** "Add a note" under an alert. Counselor/admin-only alerts get a counselor/admin-only note; the rest a note the student's teachers can read. */
function NoteBox({ studentId, restricted }: { studentId: number; restricted: boolean }) {
  const [body, setBody] = useState('');
  const create = useNoteCreate();
  const save = () => {
    const text = body.trim();
    if (!text) return;
    // A note under a counselor/admin-only alert must not reach the student's teachers.
    create.mutate({ student: studentId, body: text, visibility: restricted ? 'counselor_admin_only' : 'teachers_of_student' }, {
      onSuccess: () => { setBody(''); toast.success('Note saved'); },
      onError: () => toast.error('Could not save the note'),
    });
  };
  return (
    <div className="mt-2">
      <label htmlFor="alert-note" className="mb-2.5 block font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Add a note</label>
      <textarea id="alert-note" value={body} onChange={(e) => setBody(e.target.value)} placeholder="What did you do or decide?"
        className={`${fieldClass} min-h-[84px] resize-y py-3`} />
      <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-muted">{restricted ? 'Visible to counselors and school admins only' : "Visible to the student's teachers, counselors and admins"}</span>
        <Button variant="ghost" className="!min-h-[42px]" onClick={save} loading={create.isPending} disabled={!body.trim()}>Save note</Button>
      </div>
    </div>
  );
}

// Case category for an alert: wellbeing rules (and anything not meant for teachers) open a wellbeing
// case, study-habit rules an engagement case, score rules an academic case.
const ACADEMIC = ['academic_decline_subject', 'repeated_chapter_fail', 'peer_gap_persistent', 'class_rank_drop'];
const ENGAGEMENT = ['engagement_drop', 'late_night_study', 'streak_break', 'study_plan_skipped', 'missed_milestone', 'ai_overreliance', 'ai_cheating_suspected'];
export function caseCategory(a: Pick<AlertInstance, 'rule_code' | 'audience'>): 'academic' | 'engagement' | 'wellbeing' | 'other' {
  if (a.audience !== 'teachers' || a.rule_code.startsWith('wellbeing')) return 'wellbeing';
  if (ACADEMIC.includes(a.rule_code)) return 'academic';
  if (ENGAGEMENT.includes(a.rule_code)) return 'engagement';
  return 'other';
}

function FilterGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">{label}</span>
      {children}
    </div>
  );
}

export default function AlertsInbox() {
  const { role } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [severity, setSeverity] = useState<string>('');
  const [snoozeDays, setSnoozeDays] = useState<number>(7);
  const [gradeId, setGradeIdRaw] = useState<number | null>(null);
  const [sectionId, setSectionId] = useState<number | 'all'>('all');
  // The section belongs to the grade, so both change together (no request goes out with a stale pair).
  const setGradeId = (g: number | null) => { setGradeIdRaw(g); setSectionId('all'); };
  // Grade and section narrow the inbox server-side; severity is applied on top of them.
  const scope: { grade?: number; section?: number } = {
    ...(gradeId != null ? { grade: gradeId } : {}),
    ...(gradeId != null && sectionId !== 'all' ? { section: sectionId } : {}),
  };
  const q = useAlertsInbox({ ...(severity ? { severity } : {}), ...scope });
  const all = useAlertsInbox(scope);
  const action = useAlertAction();
  const createCase = useCaseCreate();
  const gradesQ = useQuery({ queryKey: ['analytics.grades'], queryFn: () => SchoolApi.analyticsGrades() });
  const grades = gradesQ.data?.grades ?? [];
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const items = q.data?.results ?? [];
  const pg = usePagination(items, 10, `${severity}|${gradeId}|${sectionId}`);
  // The detail always shows an alert from the visible page (the first one by default).
  const selected = pg.pageItems.find((a) => a.id === selectedId) ?? pg.pageItems[0];
  const allItems = all.data?.results ?? [];
  const changePage = (p: number) => { setSelectedId(null); pg.setPage(p); };
  const count = (sev: string) => allItems.filter((a) => a.severity === sev).length;
  // Grade and section sit on the alert once the backend sends them; until then they come from the students list.
  const needsLookup = items.some((a) => a.student_grade === undefined && a.student_section === undefined);
  const students = useStudentLookup(needsLookup);
  const detail = useAlertDetail(selected?.id);
  const notified = useMemo(() => detail.data?.notified ?? [], [detail.data]);

  function act(name: 'acknowledge' | 'snooze' | 'dismiss' | 'resolve', body: any = {}) {
    if (!selected) return;
    action.mutate(
      { id: selected.id, action: name, body },
      {
        onSuccess: () => toast.success(`${name}d`),
        onError: () => toast.error(`Failed to ${name}`),
      },
    );
  }

  function openCase() {
    if (!selected) return;
    createCase.mutate({ student: selected.student_id, category: caseCategory(selected), alert_id: selected.id }, {
      onSuccess: (c) => {
        qc.invalidateQueries({ queryKey: ['school', 'alerts'] });
        toast.success('Case opened');
        navigate(`/cases/${c.id}`);
      },
      onError: (e: any) => {
        const d = e?.response?.data;
        const first = d && typeof d === 'object' ? Object.values(d).flat().find((v) => typeof v === 'string') : null;
        toast.error((typeof d?.detail === 'string' && d.detail) || (first as string | null) || 'Could not open a case');
      },
    });
  }

  // The backend refuses a teacher a wellbeing (counselor/admin-only) case, so the button is not offered.
  // An alert already in a case links to it instead (the backend refuses a second link).
  const inCase = !!selected && (selected.status === 'in_case' || selected.case_id != null);
  const canOpenCase = !!selected && !inCase && !(role === 'teacher' && caseCategory(selected) === 'wellbeing');
  const selectedName = selected ? nameOr(selected.student_name, selected.student_username) : '';
  const selectedPlace = selected ? alertPlace(selected, students, false) : '';
  const selectedRisk = selected ? alertRisk(selected, students) : null;

  return (
    <div>
      <PageHeader
        className="!mb-[22px]"
        eyebrow="Workflow"
        title="Alerts"
        subtitle="Rules flag students whose activity, scores or check-ins need a look."
        actions={RULES_ROLES.includes(role as string) ? (
          <Link to="/settings/alerts"
                className={LINK_BTN}>
            <SlidersHorizontal size={18} strokeWidth={1.8} aria-hidden="true" />
            Alert rules
          </Link>
        ) : undefined}
      />

      {grades.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-x-[26px] gap-y-3">
          <FilterGroup label="Grade">
            <Segmented scroll label="Grade" value={gradeId == null ? 'all' : String(gradeId)}
              onChange={(k) => setGradeId(k === 'all' ? null : Number(k))}
              options={[{ key: 'all', label: 'All' }, ...grades.map((g) => ({ key: String(g.id), label: compactGrade(g.name) }))]} />
          </FilterGroup>
          {gradeId != null && (
            <FilterGroup label="Section">
              <SectionPicker grade={gradeId} value={sectionId} onChange={setSectionId} aria-label="Section" />
            </FilterGroup>
          )}
        </div>
      )}

      <PillTabs
        label="Severity"
        value={severity}
        onChange={setSeverity}
        options={[
          { key: '', label: 'All open', count: allItems.length },
          { key: 'critical', label: 'Critical', count: count('critical') },
          { key: 'warning', label: 'Warning', count: count('warning') },
          { key: 'info', label: 'Info', count: count('info') },
        ]}
      />

      <div className="mt-4 flex flex-wrap items-start gap-[18px]">
        <PanelCard className="flex-[1_1_380px] overflow-hidden !rounded-[18px] !p-0 !shadow-list">
          {items.length === 0 ? (
            <EmptyState icon={<FaBell />} title="No alerts" body="You're all caught up." />
          ) : (
            <>
            <ul>
              {pg.pageItems.map((a) => {
                const on = selected?.id === a.id;
                const place = alertPlace(a, students, false);
                return (
                  <li key={a.id} className="border-b border-hairline last:border-b-0">
                    <button type="button" aria-pressed={on} onClick={() => setSelectedId(a.id)}
                            className={`relative flex w-full gap-3 px-[18px] py-4 text-left transition-colors hover:bg-surface-2 ${on ? 'bg-accent-bg' : ''}`}>
                      {on && <span aria-hidden="true" className="absolute bottom-2.5 left-0 top-2.5 w-[3px] rounded-r-[3px] bg-navy" />}
                      <SeverityDot severity={a.severity} />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-baseline justify-between gap-2.5">
                          <span className="truncate font-semibold text-ink">{nameOr(a.student_name, a.student_username)}</span>
                          <span className="whitespace-nowrap text-xs text-muted">{whenLabel(a.fired_at)}</span>
                        </span>
                        <span className="mt-0.5 block text-muted-2">{alertSentence(a)}</span>
                        <span className="mt-1 flex items-center gap-2 text-xs text-muted">
                          <span>{[place, a.rule_name].filter(Boolean).join(' · ')}</span>
                          {a.status !== 'open' && <Badge tone="neutral">{a.status.replace(/_/g, ' ')}</Badge>}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            <PagedFooter from={pg.from} to={pg.to} total={pg.total} page={pg.page} pageCount={pg.pageCount} onPage={changePage}
                         label="Alerts pagination" className="border-t border-hairline px-[18px] py-3" />
            </>
          )}
        </PanelCard>

        <PanelCard className="flex-[1.4_1_460px] !rounded-[18px] !p-[26px] !shadow-list">
          {!selected ? (
            <p className="text-sm text-muted">Select an alert to see detail.</p>
          ) : (
            <div>
              <div className="mb-2.5 flex flex-wrap items-center gap-2">
                <AlertSeverityBadge severity={selected.severity} casing="title" />
                <span className="text-[13px] text-muted">{selected.rule_name} · raised {whenLabel(selected.fired_at)}</span>
              </div>
              <h2 className="font-display text-[22px] font-extrabold leading-[1.15] tracking-[-0.02em] text-ink sm:text-[26px]">
                {alertSentence(selected)}
              </h2>

              <AlertPayloadDetail alert={selected} hideHeadline />

              <Link to={`/students/profile/${selected.student_id}`}
                    className="flex items-center gap-3 rounded-[14px] border border-line p-3.5 no-underline transition-colors hover:bg-surface-2">
                <Initials name={selectedName} size="md" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-semibold text-ink">{selectedName}</span>
                  {(selectedPlace || selectedRisk != null) && (
                    <span className="text-xs text-muted">{[selectedPlace, selectedRisk != null ? `risk ${Math.round(selectedRisk)}` : null].filter(Boolean).join(' · ')}</span>
                  )}
                </span>
                <span className="text-[13px] font-semibold text-ink">Open profile →</span>
              </Link>

              <div className="mb-6 mt-5 flex flex-wrap items-center gap-2">
                {inCase && <Link to={selected?.case_id != null ? `/cases/${selected.case_id}` : '/cases'} className={`${LINK_BTN} !min-h-[42px]`}>View case →</Link>}
                {canOpenCase && <Button variant="secondary" className="!min-h-[42px] hover:!bg-navy hover:!text-accent" onClick={openCase} loading={createCase.isPending}>Open a case</Button>}
                <Button variant="ghost" className="!min-h-[42px]" onClick={() => act('acknowledge')}>Acknowledge</Button>
                <Button variant="ghost" className="!min-h-[42px]" onClick={() => act('resolve')}>Resolve</Button>
                <div className="inline-flex flex-wrap items-center gap-1.5">
                  <Button variant="ghost" className="!min-h-[42px]" onClick={() => act('snooze', { days: snoozeDays })}>Snooze</Button>
                  <Segmented dense label="Snooze days" value={String(snoozeDays)} onChange={(k) => setSnoozeDays(Number(k))}
                             options={[1, 3, 7, 14].map((n) => ({ key: String(n), label: `${n}d` }))} />
                </div>
                <Button variant="ghost" className="ml-auto !min-h-[42px] !text-danger-ink" onClick={() => act('dismiss')}>Dismiss</Button>
              </div>

              <Timeline alert={selected} notified={notified} />
              <NoteBox key={selected.id} studentId={selected.student_id} restricted={selected.audience !== 'teachers'} />
            </div>
          )}
        </PanelCard>
      </div>
    </div>
  );
}
